
import React from 'react';
import { motion } from 'framer-motion';
import { Building, User, LandPlot, Search } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';

interface AudienceCardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  delay: number;
}

const AudienceCard: React.FC<AudienceCardProps> = ({ title, description, icon, delay }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: delay * 0.15 }}
      viewport={{ once: true }}
      className="relative"
    >
      <Card className="p-6 h-full bg-gradient-to-br from-white to-blue-50/50 backdrop-blur-sm hover:shadow-lg transition-all duration-300 border border-blue-100/50 overflow-hidden">
        <div className="relative z-10">
          <div className="flex items-center mb-4">
            <div className="p-2 rounded-lg bg-blue-50 text-blue-dark mr-3">
              {icon}
            </div>
            <h3 className="text-xl font-semibold text-blue-dark">{title}</h3>
          </div>
          <Separator className="my-3 bg-blue-100/50" />
          <p className="text-gray-600">{description}</p>
        </div>
        <div className="absolute top-0 right-0 opacity-5 text-blue-light">
          {icon && React.cloneElement(icon as React.ReactElement, { size: 120 })}
        </div>
      </Card>
    </motion.div>
  );
};

const AudienceSection: React.FC = () => {
  const audiences = [
    {
      title: "Imobiliárias",
      description: "Acelere o fluxo de negócios e aumente a eficiência da sua equipe com dados estratégicos e análises detalhadas sobre imóveis e terrenos em Campo Grande/MS. Ofereça aos seus clientes informações precisas e torne-se referência no mercado local.",
      icon: <Building className="h-6 w-6" />,
    },
    {
      title: "Corretores de Imóveis",
      description: "Tenha em mãos informações que agilizam e otimizam o seu trabalho. O Broker Base proporciona insights precisos e georreferenciados que aumentam sua credibilidade perante os clientes e facilitam negociações mais rápidas e assertivas.",
      icon: <User className="h-6 w-6" />,
    },
    {
      title: "Incorporadores",
      description: "Encontre terrenos estratégicos com facilidade e segurança. Utilize as análises detalhadas e os dados cruzados do Broker Base para tomar decisões informadas e identificar oportunidades que maximizam o retorno sobre investimentos imobiliários.",
      icon: <LandPlot className="h-6 w-6" />,
    },
    {
      title: "Investidores",
      description: "Não é preciso ser um especialista para fazer análises robustas. Seja você um investidor independente ou alguém buscando o imóvel perfeito, o Broker Base simplifica o acesso às informações detalhadas de imóveis e terrenos, garantindo uma tomada de decisão informada e segura.",
      icon: <Search className="h-6 w-6" />,
    },
  ];

  return (
    <section className="py-16 md:py-24 relative overflow-hidden">
      
      <div className="container mx-auto px-4 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-blue-dark via-blue-main to-blue-light inline-block">
            Para quem é o Broker Base?
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            O Broker Base é a plataforma ideal para profissionais e investidores do mercado imobiliário
          </p>
        </motion.div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
          {audiences.map((audience, index) => (
            <AudienceCard 
              key={index}
              title={audience.title}
              description={audience.description}
              icon={audience.icon}
              delay={index}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default AudienceSection;
